/* eslint-disable @typescript-eslint/no-unused-vars,no-underscore-dangle */
import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';

import { SkipPayBaseElement } from './base';
import { TranslationKey } from './translations/types';
import { Currency, Language, Product } from './types';

@customElement('sp-modal')
export class SPModal extends SkipPayBaseElement {
	static override get styles() {
		return css`
			:host {
				font-family: 'Poppins', sans-serif;
			}
			dialog::backdrop {
				background: rgba(0, 0, 0, 0.5);
			}

			dialog {
				border-radius: 12px;
				max-width: 640px;
				padding: 32px 16px;
				border: none;
				color: #37474f;
			}

			@media (max-width: 768px) {
				dialog {
					max-width: calc(100% - 64px);
				}
			}

			h1,
			h2 {
				margin: 0;
				margin-bottom: 8px;
				text-align: center;
				color: #253044;
				line-height: 150%;
			}

			h1 {
				font-size: 24px;
				font-weight: 600;
			}

			@media (max-width: 768px) {
				h1 {
					font-size: 18px;
				}
			}

			h2 {
				font-size: 16px;
				font-weight: 400;
			}

			.close {
				position: absolute;
				right: 16px;
				top: 32px;
				background: none;
				border: none;
				padding: 0;
				width: 28px;
				height: 28px;
				cursor: pointer;
			}
			.logo {
				display: flex;
				justify-content: center;
				margin-bottom: 24px;
			}

			a {
				color: #1e82f0;
				text-decoration: underline;
				text-align: center;
				display: block;
			}

			ul {
				list-style: none;
				margin-block-end: 1rem;
				margin-block-start: 1rem;
				padding: 0;
			}

			li {
				display: flex;
				align-items: center;
				border-radius: 8px;
				background: #f3f8fe;
				margin-bottom: 1rem;
				padding: 16px;
				padding-left: 48px;
				position: relative;
				margin-left: 30px;
				min-height: 28px;
				font-size: 14px;
			}

			.number {
				position: absolute;
				width: 60px;
				height: 60px;
				display: flex;
				justify-content: center;
				align-items: center;
				border-radius: 50%;
				background: #e8f2fd;
				top: calc(50% - 30px);
				left: -30px;
				color: #1e82f0;
				font-size: 20px;
				font-weight: 600;
			}

			.third {
				display: flex;
				justify-content: center;
				flex-direction: column;
				align-items: center;
				gap: 8px;
			}

			.thirds {
				display: flex;
				justify-content: center;
				flex-direction: row;
				justify-content: space-evenly;
				padding-top: 24px;
				padding-bottom: 24px;
			}

			.installmentDescription {
				color: #78909c;
				font-family: Poppins;
				font-size: 12px;
				font-style: normal;
				font-weight: 500;
				line-height: 150%;
			}

			.amount {
				color: #253044;
				font-family: Poppins;
				font-size: 12px;
				font-style: normal;
				font-weight: 600;
				line-height: 150%;
			}

			.stacked-text {
				display: flex;
				flex-direction: column;
				align-items: center;
			}
			.stacked-text p {
				margin: 0;
			}
		`;
	}

	@property({ type: String })
	product: Product = 'PAY_IN_THREE';

	@property({ type: Number })
	amount?: number = 3000;

	@property({ type: String })
	currency?: Currency = 'CZK';

	@property({ attribute: false })
	dialogRef: Ref<HTMLDialogElement> = createRef();

	@property({ type: Boolean })
	withLandingLink!: boolean;

	@property({ type: String })
	language: Language = 'cs';

	@property({ type: Object })
	translations!: Record<TranslationKey, string>;

	@property({ type: String })
	merchantName: string = window.location.hostname;

	private _getTexts() {
		if (this.product === 'PAY_IN_THREE') {
			return {
				mainTitle: this.translations.PAY_IN_THREE_MODAL_TITLE,
				subTitle: this.translations.PAY_IN_THREE_MODAL_SUB_TITLE,
				closeAlt: this.translations.CLOSE_MODAL,
				steps: [
					this.currency && this.amount
						? this.translations.PAY_IN_THREE_MODAL_STEP_1
						: this.translations.PAY_IN_THREE_MODAL_STEP_1_DYNAMIC,
					this.translations.PAY_IN_THREE_MODAL_STEP_2,
					this.translations.PAY_IN_THREE_MODAL_STEP_3,
				],
				payToday: this.translations.PAY_IN_THREE_MODAL_PAY_TODAY,
				payUntil: this.translations.PAY_IN_THREE_UNTIL,
			};
		}
		return {
			mainTitle: this.translations.DEFERRED_PAYMENT_MODAL_TITLE,
			subTitle: this.translations.DEFERRED_PAYMENT_MODAL_SUB_TITLE,
			closeAlt: this.translations.CLOSE_MODAL,
			steps: [
				this.translations.DEFERRED_PAYMENT_MODAL_STEP_1,
				this.translations.DEFERRED_PAYMENT_MODAL_STEP_2,
				this.translations.DEFERRED_PAYMENT_MODAL_STEP_3,
			],
		};
	}

	private _getProductPageLink() {
		if (this.product === 'PAY_IN_THREE') {
			switch (this.language) {
				case 'hu':
					return 'https://www.skippay.hu/pay-in-three';
				case 'sk':
					return 'https://skippay.sk/nakup-na-tretinu/';
				default:
					return 'https://skippay.cz/nakup-na-tretinu/';
			}
		}
		if (this.language === 'cs') {
			return 'https://skippay.cz/platba-skip-pay/';
		}
		return 'https://skippay.sk/odlozena-platba/';
	}

	private _getLandingLink() {
		const link = this._getProductPageLink();

		const utmParams = {
			utm_source: this.getMerchantName(this.merchantName),
			utm_medium: 'embed_widget',
			utm_campaign: 'modal',
		};

		const params = new URLSearchParams(utmParams);

		return `${link}?${params.toString()}`;
	}

	private _onDialogClick(event: MouseEvent) {
		// Check if the click target is the dialog itself (backdrop)
		if (event.target === this.dialogRef.value) {
			this.dialogRef.value?.close();
		}
	}

	private _getLogo() {
		const analyticsParams = {
			pl: 'd',
			m: this.getMerchantName(this.merchantName),
			p: this.product === 'PAY_IN_THREE' ? '3' : 'd',
			t: this.product === 'PAY_IN_THREE' && this.currency && this.amount ? '3c' : '3b',
		};

		const logoUrl = new URL(`${process.env.ASSETS_BASE_URL}/logo-modal.svg`);
		logoUrl.search = new URLSearchParams(analyticsParams).toString();

		return logoUrl.toString();
	}

	private _getThirdsLogo(step: number) {
		return `${process.env.ASSETS_BASE_URL}/third-${step}.png`
	}

	private _getArrow() {
		return `${process.env.ASSETS_BASE_URL}/arrow.png`;
	}

	private _closeDialog() {
		this.dialogRef.value?.close();
	}

	private _getFutureDates(): [string, string] {
		const now = new Date();
		const oneMonthLater = new Date(now);
		oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);
		const twoMonthsLater = new Date(now);
		twoMonthsLater.setMonth(twoMonthsLater.getMonth() + 2);
		const formatDate = (date: Date): string => {
			const day = date.getDate();
			const month = date.getMonth() + 1;
			const year = date.getFullYear();
			return `${day}. ${month}. ${year}`;
		};
		return [formatDate(oneMonthLater), formatDate(twoMonthsLater)];
	}

	private _getPayInThreeSection() {
		const { payToday, payUntil } = this._getTexts();
		const [firstInstallment, secondInstallment, thirdInstallment] = this.calculateInstallments(
			this.amount as number
		);
		const [firstDueDate, secondDueDate] = this._getFutureDates();

		const renderThird = (logoStep: number, amount: number, description: string) => html`
			<div class="third">
				<img
					width="60"
					height="61"
					src=${this._getThirdsLogo(logoStep)}
					alt="third-${logoStep}"
					loading="lazy"
				/>
				<div class="stacked-text">
					<p class="amount">
						<b
							>${this.formatAmountForLocale(amount, this.language)}
							${this.getCurrencySymbol(this.currency)}</b
						>
					</p>
					<p class="installmentDescription">${description}</p>
				</div>
			</div>
		`;

		return html`
			<div class="thirds">
				${renderThird(1, firstInstallment, payToday ?? '')}
				<img width="12" height="89" src=${this._getArrow()} alt="nextThird" loading="lazy" />
				${renderThird(2, secondInstallment, `${payUntil} ${firstDueDate}`)}
				<img width="12" height="89" src=${this._getArrow()} alt="nextThird" loading="lazy" />
				${renderThird(3, thirdInstallment, `${payUntil} ${secondDueDate}`)}
			</div>
		`;
	}

	override render() {
		const { mainTitle, steps, subTitle, closeAlt } = this._getTexts();

		return html`
			<dialog
				${ref(this.dialogRef)}
				@click=${this._onDialogClick}
				aria-labelledby="skippay-modal-title"
				aria-describedby="skippay-modal-description"
			>
				<div role="document">
					<button class="close" @click=${this._closeDialog} aria-label=${closeAlt}>
						<img
							width="28"
							height="28"
							src=${`${process.env.ASSETS_BASE_URL}/cross.svg`}
							alt=${closeAlt}
							loading="lazy"
						/>
					</button>

					<div class="logo">
						<img width="96" height="45" src=${this._getLogo()} alt="Skip Pay" loading="lazy" />
					</div>
					<h1 id="skippay-modal-title">${mainTitle}</h1>
					<h2 id="skippay-modal-description">${subTitle}</h2>
					${this.product === 'PAY_IN_THREE' && this.currency && this.amount
				? this._getPayInThreeSection()
				: ''}
					<ul>
						${steps.map(
					(step, index) =>
						html`<li>
									<span class="number" aria-hidden="true">${index + 1}</span
									><span class="content">${step}</span>
								</li>`
				)}
					</ul>

					${this.withLandingLink
				? html`<a href=${this._getLandingLink()} target="_blank" tabindex="0"
								>${this.translations.MODAL_MORE_INFO}</a
							>`
				: ''}
				</div>
			</dialog>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'sp-modal': SPModal;
	}
}
